import { Dictionary } from "@ngrx/entity"
import { createSelector } from "@ngrx/store"
import { DayFull } from "globals/helpers/constants"
import { flatten, makeMapFromListOfObjectsAndKey, } from "globals/helpers/helpers"
import { AssetsBaseTableData, AssetTableData } from "../../helpers/table-column-types"
import { assetColumnsById, allDefaultAssetColumns, tableLightColors } from "../../helpers/table-helpers"
import { TableColumn } from "../../helpers/table.data"
import { Asset } from "globals/store/assets.data"
import { Label, Ranking } from "globals/store/events.data"
import { FilterGroup, FiltersState, IToggleFilter } from "../filters.era"
import { columnOrderTypes } from "../settings.era"
import { User } from "globals/store/user.data"
import { WorkerWithProfile } from "globals/store/workforce.data"
import { assetListReselector, entitiesReselector } from "./entity.reselector"
import { assetSessionListReselector } from "./sessions.reselector"
import { jsonCopyObject } from 'globals/helpers/helpers';
import { programsArray } from "./programs.reselector"
import { fullSingleTimelineReselector } from "./onsiteTimeline.reselector"
import { ManageActivityData } from "globals/store/onsite.data"
import { formatDate } from "@angular/common"
import { Informable, InformableSectionItem } from "globals/store/informables.data"
import { getBaseAssetTableData, getBaseTemplateTableData } from "./entity.decircular.reselector"
import { entitiesByAssetIdsTransformer } from "globals/store/share.brief.functions"
import { exhibitorStandListReselector } from "./exhibitors.reselector"
import { environment } from "../../../environments/environment"
import { shareUrlsByType } from "globals/store/share.data"
import { DropdownOption, Program } from "globals/store/programs.data"
import { makePrettyNumber } from "../../services/pipes/prettyNumbers.pipe"
import { nameJoiner } from "src/app/helpers/helpers"
import { tasksStateSelector } from "../tasks.era"

const assets = (store)=> store.assets.entities
const assetIds = (store)=> store.assets.ids

const user = (store) => store.user.user
const currentEvent = (store) => store.events.event
const filters = (store) => store.filters
const workersByEmail = (store) => store.workforce.workersByEmail
const workersByUId = (store) => store.workforce.workersByUId
const assetLabels = (store) => store.events.labelMaps.asset_labels
const userSettings = (store) => store.settings.user
const eventInformables = (store) => store.informables.event
const performerInformationInformables = (store) => store.informables.performers
const exhibitorInformationInformables = (store) => store.informables.exhibitors
const resourceInformationInformables = (store) => store.informables.resources
const tasks = createSelector(tasksStateSelector, (state) => state.entities)
const assetCategories = (store) => store.assets.assetCategories

interface AssetPreselectorData { user: User, workersByEmail: { [key:string]: WorkerWithProfile }, workersByUId: { [key:string]: WorkerWithProfile }, filterState: FiltersState, assetLabels: Dictionary<Label>, assets: Dictionary<Asset>, assetCategories: Dictionary<Ranking> }

export const assetCategoriesDropdownReselector = createSelector(
  currentEvent,
  (currentEvent): DropdownOption[] => [{ id: null, title: 'No Asset Category'}, ...currentEvent.assetCategories.map(c => ({ id: c.id, title: c.name }))]
)

export const activitiesByAssetIdsReselector = createSelector(
  fullSingleTimelineReselector,
  assets,
  (timelineDays, a) => {
    const allActivityInfosOnAssets = flatten(timelineDays.filter(d => !!d.date).map(({ date, bricks }) => 
      bricks.filter(b => b.type === 'activity' && b.assetIds?.length).map((b): ManageActivityData => ({ 
        activityId: b.id, scheduleTitle: b.schedule_title, date, location: b.program_location, assetIds: b.assetIds,
        assetTitle: `${ b.isStandalone ? '' : (b.program_title || 'Unnamed Room/Location') + ' - ' }${ b.schedule_title || 'Unnamed Schedule' } - ${b.title || 'Unnamed Activity'} - ${formatDate(date, 'EEE d MMMM y', "en-US")} ${b.time}`
      }))
    ))
    // 
    return entitiesByAssetIdsTransformer(allActivityInfosOnAssets, 'assetIds', a, 'assetTitle')
  }
)

export const programsByAssetIdsReselector = createSelector(
  programsArray,
  assets,
  (p, a) => entitiesByAssetIdsTransformer(p, 'assetIds', a, 'title')
)

export const setupsByAssetIdsReselector = createSelector(
  programsArray,
  assets,
  (programs, a) => {
    const transformerInputs = flatten(programs.map(p => Object.values(p.setups || {}).map(s => ({ assetIds: s.assetIds || [], name: `${p.title || 'Untitled Program'} - ${s.name || 'Unnamed Setup'}`, programId: p.id }))))
    const setupsByAssetIds = entitiesByAssetIdsTransformer(transformerInputs, 'assetIds', a, 'name')
    // console.log({ transformerInputs, programsArray, setupsByAssetIds })
    return setupsByAssetIds
  }
)

export const runOfShowsByAssetIdsReselector = createSelector(
  assetSessionListReselector,
  assets,
  (sessions, a) => {
    const transformerInputs = sessions.map(s => {
      const allAssetIds = flatten(s.cues.rows.map(r => Object.values(r.cues).map(r => r.assetId))).filter(id => !!id)
      const countByAssetIds = {}
      allAssetIds.forEach(id => {
        if (!countByAssetIds[id]) countByAssetIds[id] = 0
        countByAssetIds[id] += 1
      })
      
      return { assetIds: Array.from(new Set(allAssetIds)), name: s.assetSessionTitle, sessionId: s.id, countByAssetIds }
    })
    const runOfShowsByAssetIds = entitiesByAssetIdsTransformer(transformerInputs, 'assetIds', a, 'name')
    console.log({ runOfShowsByAssetIds })
    return runOfShowsByAssetIds
  }
)

export const sessionsByAssetIdsReselector = createSelector(
  assetSessionListReselector,
  assets,
  (s, a) => entitiesByAssetIdsTransformer(s, 'assetIds', a, 'assetSessionTitle')
)

export const sessionsByPresentationAssetIdsReselector = createSelector(
  assetSessionListReselector,
  assets,
  (s, a) => entitiesByAssetIdsTransformer(s, 'presentationAssetIds', a, 'assetSessionTitle')
)

export const exhibitorStandsByAssetIdsReselector = createSelector( 
  exhibitorStandListReselector,
  assets,
  (e, a) => entitiesByAssetIdsTransformer(e, 'assetIds', a, 'name')
)

const transformInformableToAssetSectionItems = (EI: Informable, a: Dictionary<Asset>): InformableSectionItem[] => flatten(EI.info.map(info => {
  const itemsWithAssets = info.items
    .filter(i => i.assetIds && i.assetIds.filter(id => !!a[id]).length)
    .map(i => jsonCopyObject(i)) // copy that isn't directly passed
  itemsWithAssets.forEach(i => {
    i.assetItemTitle = `${info.title || 'Unnamed section'} - ${i.name || 'Unnamed item' }` 
    i.informable = EI // just a reference.  Item was shallow
    i.section = info
  })
  return itemsWithAssets
}))

export const eventInformationItemsByAssetIdsReselector = createSelector(
  eventInformables,
  assets,
  (EI, a) => {
    return entitiesByAssetIdsTransformer(!EI ? [] : transformInformableToAssetSectionItems(EI, a), 'assetIds', a, 'assetItemTitle')
  }
)

export const performerInformationItemsByAssetIdsReselector = createSelector(
  performerInformationInformables,
  assets,
  (PI, a) => {
    return entitiesByAssetIdsTransformer(!PI ? [] :transformInformableToAssetSectionItems(PI, a), 'assetIds', a, 'assetItemTitle')
  }
)

export const exhibitorInformationItemsByAssetIdsReselector = createSelector(
  exhibitorInformationInformables,
assets,
  (EI, a) => {
    return entitiesByAssetIdsTransformer(!EI ? [] :transformInformableToAssetSectionItems(EI, a), 'assetIds', a, 'assetItemTitle')
  }
)

export const resourceInformationItemsByAssetIdsReselector = createSelector(
  resourceInformationInformables,
  assets,
  (RI, a) => {
    return entitiesByAssetIdsTransformer(!RI ? [] :transformInformableToAssetSectionItems(RI, a), 'assetIds', a, 'assetItemTitle')
  }
)

const addToCounts = (entities: Dictionary<any[]>, counts: Dictionary<number>) => 
  Object.entries(entities).forEach(([key, array]) => counts[key] += array.length)


const allAssetsAttachedToPrePreselector = createSelector( // because can only have 9 inputs
  activitiesByAssetIdsReselector,
  programsByAssetIdsReselector,
  sessionsByAssetIdsReselector,
  sessionsByPresentationAssetIdsReselector,
  exhibitorStandsByAssetIdsReselector,
  eventInformationItemsByAssetIdsReselector,
  performerInformationItemsByAssetIdsReselector,
  exhibitorInformationItemsByAssetIdsReselector,
  (a, b, c, d, e, f, g, h ) => [a,b,c,d,e,f,g,h]
)

const allAssetAttachedToPreselector = createSelector(
  allAssetsAttachedToPrePreselector,
  setupsByAssetIdsReselector,
  (abcdefgh, i) => [...abcdefgh, i]
)

export const attachedToCountByAssetIdReselector = createSelector(
  allAssetAttachedToPreselector,
  assetIds,
  runOfShowsByAssetIdsReselector,
  (attachedTos, assetIds: string[], runOfShowsByAssetIds) => {
    const counts: Dictionary<number> = {}
    assetIds.forEach(id => counts[id] = 0)
    attachedTos.forEach(a => addToCounts(a, counts))
    Object.values(runOfShowsByAssetIds).forEach(a => a.forEach(b => Object.entries(b.countByAssetIds).forEach(([assetId, count]: [ string, number ]) => {
      counts[assetId] += count
    })))
    return counts
  }
)


export const assetsPreselector = createSelector( // cause 10 args is max
  assets,
  user,
  workersByEmail,
  workersByUId,
  filters,
  assetLabels,
  assetCategories,
  (assets, user: User, workersByEmail, workersByUId, filterState: FiltersState, assetLabels: Dictionary<Label>, assetCategories): AssetPreselectorData => {
    return { assets, user, workersByEmail, workersByUId, filterState, assetLabels, assetCategories }
  }
)



export const assetColumnsReselector = createSelector(
  userSettings,
  (settings) => {
    const selectedColumns = !settings ? null : settings[`${columnOrderTypes.asset_table}_columns`]
    // console.log({ selectedColumns, settings })
    return (!selectedColumns ? allDefaultAssetColumns : selectedColumns.filter(c => c.selected).map(c => assetColumnsById[c.value])).filter((c: TableColumn) => !c.notOuter)
  }
)


export const filterAssets = (assets: Asset[], filters: FilterGroup, toggleFilters: IToggleFilter, userYou: User, programsByAssetId: Dictionary<Program[]>): Asset[] => assets.filter(a => {
  const createdByMe = filters.me && a.createdBy === userYou.uid
  const flagged = a.flagged// && filters.flagged && a.flag[filters.flagged]
  const myResponsibility = filters.myResponsibility && a.responsibility === userYou.email
  const anyMeta = createdByMe || flagged || myResponsibility
  const programIds = programsByAssetId[a.id].map(p => p.id)
  const types = filters.types
  const categories = filters.categories

  return ![
    () => !filters.types?.length || types.includes(a.icon),
    () => !filters.categories?.length || categories.includes(a.entityTypeId),
    () => !toggleFilters.sortByDate || a.dueDate,
    () => !filters.me || anyMeta,
    () => !filters.myResponsibility || anyMeta,
    () => !filters.flagged || anyMeta,
    () => !filters.entityIds || filters.entityIds.includes(a.associatedId),
    () => !filters.responsibilities || myResponsibility || filters.responsibilities.includes(a.responsibility),
    () => !filters.createdBy || createdByMe || filters.createdBy.includes(a.createdBy),
    () => !filters.programs || programIds.some(id => filters.programs.includes(id)),
    () => !filters.assetLabels || (a.labels && a.labels.some(v => filters.assetLabels.includes(v))),
  ].some(passesFilterFunction => !passesFilterFunction()) 
})

export const assetTableReselector = createSelector(
  assetsPreselector,
  assetListReselector,
  entitiesReselector,
  attachedToCountByAssetIdReselector,
  currentEvent,
  programsByAssetIdsReselector,
  tasks,
  attachedToCountByAssetIdReselector,
  ({ assets, workersByEmail, user, workersByUId, filterState, assetLabels, assetCategories }, assetList, entities, attachedToCounts, currentEvent, programsByAssetId, tasks, attachedCounts) => {
  

  const filters = filterState.asset_table
  const toggleFilters = filterState.toggleFilters.asset_table
  assetList = filterAssets(assetList, filters, toggleFilters, user, programsByAssetId)
  // console.log({ filteredTasks: jsonCopyObject(tasks), toggleFilters, filters, user, eventIds: filteredEvents.eventIds })

  if (filters.search) {
    const searchTerm = filters.search.toLocaleLowerCase()
    assetList = assetList.filter(a => a.name && a.name.toLocaleLowerCase().includes(searchTerm))
  }

  const taskList = Object.values(tasks).filter(t => !!assets[t.entityId]) // any tasks with a current asset
  const assetTableData = assetList.map(asset => {
    const { flagged, responsibility, dueDate, ready, labels, associatedId, createdBy, status, eventId, showInBudget, createdType, id, quantity } = asset

    const date = !dueDate ? null : new Date(dueDate)
    const now = new Date()
    const responsible = workersByEmail[responsibility]
    const entity = entities[associatedId]
    const creator = workersByUId[createdBy]

    const creatorEntity = createdType ? entities[createdBy] : null

    const associatedTasks = taskList.filter(t => t.entityId === id)
    const completedTasks = associatedTasks.filter(t => t.completed).length
    const completedTasksPercent = !associatedTasks.length ? 0 : completedTasks / associatedTasks.length
    const category = assetCategories[asset.entityTypeId]

    const assetRow: AssetTableData = {
      ...getBaseAssetTableData(asset),
      category: { text: category?.name || '' },
      used: { text: `${attachedToCounts[id]}` },
      tasks: !associatedTasks.length ? null :{
        percent: completedTasksPercent,
        lights: [{
          text: `${completedTasks} / ${associatedTasks.length}`,
          color: completedTasksPercent > 0.999 ? tableLightColors.green : tableLightColors.yellow,
        }]},
      editingURL: { url: status === 'Off' ? null : 
        `${environment.joi.shareBase}/${shareUrlsByType.fullAsset}/${eventId || currentEvent?.eventId }/${id}`},
      creator: !creator ? (creatorEntity ? { name: creatorEntity.name, createdType, hidePhoto: true } : null) : {
        name: nameJoiner(creator),
        id: creator.uid,
        email: creator.email,
        photoUrl: creator.photoURL,
        fullPhotoUrl: creator.photoURL,
      },
      attachedTo: { text: `${attachedCounts[id]}` },
      associatedWith: { entity },
      labels: !labels ? [] : labels.filter(l => assetLabels[l]).map(label => ({ color: assetLabels[label].color, label: assetLabels[label].label })),
      flagged: { flagged },
      responsibility: !responsible ? null : { 
        name: nameJoiner(responsible),
        id: responsible.uid || null,
        email: responsible.email,
        photoUrl: responsible.photoURL || null,
        fullPhotoUrl: responsible.photoURL || null,
      },
      quantity: !quantity ? null : { text: makePrettyNumber(quantity, 2, 1, false, true) },
      linked: { text: showInBudget ? 'Yes' : 'No' },
      dueBy: !date ? null : { date, HHMMText: `${DayFull[date.getDay()]}, `, red: date < now && !ready },
      ready: {
        percent: ready ? 1 : 0,
        lights: [{
          text: ready ? 'Ready' : 'Not ready',
          color: ready ? tableLightColors.green : tableLightColors.yellow,
        }]}
    }
    return assetRow
  })
  const assetTableRowsById = makeMapFromListOfObjectsAndKey(assetTableData, 'id')
  // console.log(`It took ${Date.now() - start}ms to reselect the task data one time (for ${taskTableData.length} tasks)`)
  return { assetTableData, assetTableRowsById }

  

})